import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/user';

import { LoadingState } from '../common/LoadingState';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireAuth?: boolean;
}

// Extend Window interface for Zoho SalesIQ
declare global {
  interface Window {
    $zoho?: {
      salesiq?: {
        ready: () => void;
      };
    };
  }
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requireAuth = true,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Load Zoho SalesIQ widget for authenticated users
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Initialize Zoho SalesIQ
      window.$zoho = window.$zoho || {};
      window.$zoho.salesiq = window.$zoho.salesiq || {
        ready: function () {},
      };

      // Load Zoho SalesIQ script if not already loaded
      if (!document.getElementById('zsiqscript')) {
        const script = document.createElement('script');
        script.id = 'zsiqscript';
        script.src =
          'https://salesiq.zohopublic.com/widget?wc=siq4d189bc85ad368c4e15da81147468534b8fd6119f917def83f5804789a4ce5ed';
        script.defer = true;
        document.body.appendChild(script);
      }
    }

    // Cleanup: Remove script when user logs out
    return () => {
      if (!isAuthenticated) {
        const script = document.getElementById('zsiqscript');
        if (script) {
          script.remove();
        }
      }
    };
  }, [isAuthenticated, isLoading]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingState message="" size="large" fullHeight={true} className="py-12" />;
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user is authenticated but accessing login page, redirect to dashboard
  if (!requireAuth && isAuthenticated && user) {
    // Block employees and clients from accessing the platform
    if (user.role === 'employee' || user.role === 'client') {
      return <Navigate to="/login" replace />;
    }

    let redirectPath = '/dashboard';
    switch (user.role) {
      case 'super_admin':
        redirectPath = '/dashboard';
        break;
      case 'admin':
      case 'manager':
        redirectPath = '/dashboard-empresarial';
        break;
      case 'admin_onboarding':
        redirectPath = '/admin-onboarding/funnel';
        break;
    }
    return <Navigate to={redirectPath} replace />;
  }

  // If specific roles are required, check user role
  if (allowedRoles && user) {
    if (!allowedRoles.includes(user.role as UserRole)) {
      let redirectPath = '/login';
      switch (user.role) {
        case 'super_admin':
          redirectPath = '/dashboard';
          break;
        case 'admin':
        case 'manager':
          redirectPath = '/dashboard-empresarial';
          break;
        case 'admin_onboarding':
          redirectPath = '/admin-onboarding/funnel';
          break;
        case 'employee':
        case 'client':
          redirectPath = '/login';
          break;
      }
      return <Navigate to={redirectPath} replace />;
    }
  }

  return <>{children}</>;
};
